import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Collapse
} from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

import useDebounce from "../../../hooks/useDebounce";
import { DEVICE_TYPE } from "../../../domain/device";
import { ROLES, useAuth } from "../../../auth";
import { useContentTypes } from "../../../domain/contentType";
import I18nText from "../../../elements/I18nText";

import ActiveFilterChips from "./ActiveFilterChips";
import DeviceTypeFilter from "./DeviceTypeFilter";
import AdvancedFilters from "./AdvancedFilters";
import FilterDialog from "./FilterDialog";
import useDeviceStatus from "../../../domain/device/useDeviceStatus";

const TYPE_BUTTONS = [
  { label: <I18nText>topbar.filter.all</I18nText>, value: "all" },
  { label: "SMS", value: "type:" + DEVICE_TYPE.SMS },
  { label: "IoT", value: "type:" + DEVICE_TYPE.IOT },
  { label: "LX-IoT", value: "type:" + DEVICE_TYPE.LX_IOT },
  { label: "NET", value: "type:" + DEVICE_TYPE.NET }
];

const SearchTermFilter = ({
  onFiltersChange,
  onSetSearchTerm,
  onSetTypeFilter,
  onSetAdvancedFilters,
  initialFilters = {},
  showActiveFilters = true,
  showAdvancedByDefault = false
}) => {
  const [filters, setFilters] = useState({
    searchTerm: initialFilters.searchTerm || "",
    typeFilter: initialFilters.typeFilter || "all",
    medium: initialFilters.medium || "",
    status: initialFilters.status || ""
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(showAdvancedByDefault);

  const debouncedSearchTerm = useDebounce(filters.searchTerm, 600);
  const { user, hasRole } = useAuth();
  const contentTypes = useContentTypes();
  const deviceStatuses = useDeviceStatus();

  const filterOptions = {
    mediumOptions: [...contentTypes],
    statusOptions: [...deviceStatuses]
  };

  const createFilterObject = useCallback(() => {
    const activeFilters = {
      ...filters,
      searchTerm: debouncedSearchTerm,
      typeFilter: filters.typeFilter === "all" ? "" : filters.typeFilter
    };

    return {
      ...activeFilters,
      hasActiveFilters: !!(
        debouncedSearchTerm ||
        filters.typeFilter !== "all" ||
        filters.medium ||
        filters.status
      ),
      activeFilterCount: [
        debouncedSearchTerm,
        filters.typeFilter !== "all" ? filters.typeFilter : "",
        filters.medium,
        filters.status
      ].filter(Boolean).length
    };
  }, [filters, debouncedSearchTerm]);

  // Update filters helper
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Clear specific filter
  const handleClearFilter = useCallback(
    filterType => {
      const resetValues = {
        searchTerm: "",
        typeFilter: "all",
        medium: "",
        status: ""
      };

      if (resetValues.hasOwnProperty(filterType)) {
        updateFilter(filterType, resetValues[filterType]);
      }
    },
    [updateFilter]
  );

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    setFilters({
      searchTerm: "",
      typeFilter: "all",
      medium: "",
      status: ""
    });
  }, []);

  // Notify parent of filter changes
  useEffect(() => {
    const filterObject = createFilterObject();

    // New unified callback
    if (onFiltersChange) {
      onFiltersChange(filterObject);
    }

    // Backward compatibility callbacks
    if (onSetSearchTerm) {
      onSetSearchTerm(debouncedSearchTerm);
    }
    if (onSetTypeFilter) {
      onSetTypeFilter(filters.typeFilter === "all" ? "" : filters.typeFilter);
    }
    if (onSetAdvancedFilters) {
      onSetAdvancedFilters({
        medium: filters.medium,
        status: filters.status
      });
    }
  }, [
    filters,
    debouncedSearchTerm,
    onFiltersChange,
    onSetSearchTerm,
    onSetTypeFilter,
    onSetAdvancedFilters,
    createFilterObject
  ]);

  const currentFilters = createFilterObject();

  return (
    <Box display="flex" flexDirection="column">
      <Box
        display="flex"
        flex="1 1 auto"
        justifyContent="flex-start"
        alignItems="center"
        marginBottom={1}
      >
        <TextField
          id="filter"
          label={<I18nText>topbar.search</I18nText>}
          type="search"
          variant="outlined"
          size="small"
          autoComplete="off"
          value={filters.searchTerm}
          onChange={e => updateFilter("searchTerm", e.target.value)}
        />
        {/** Disabled Filter button until the backend is ready. */}
        {/*<Box marginLeft={1}>*/}
        {/*  {!showAdvancedByDefault && (*/}
        {/*    <Button*/}
        {/*      aria-label="open filters"*/}
        {/*      variant={*/}
        {/*        currentFilters.hasActiveFilters ? "contained" : "outlined"*/}
        {/*      }*/}
        {/*      color="primary"*/}
        {/*      startIcon={<FilterListIcon />}*/}
        {/*      onClick={() => setDialogOpen(true)}*/}
        {/*    >*/}
        {/*      <I18nText>topbar.filter.filters</I18nText>{" "}*/}
        {/*      {currentFilters.activeFilterCount > 0 &&*/}
        {/*        `(${currentFilters.activeFilterCount})`}*/}
        {/*    </Button>*/}
        {/*  )}*/}

        {/*  {showAdvancedByDefault && (*/}
        {/*    <Button*/}
        {/*      aria-label="toggle advanced filters"*/}
        {/*      variant="outlined"*/}
        {/*      color="primary"*/}
        {/*      startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}*/}
        {/*      onClick={() => setShowAdvanced(!showAdvanced)}*/}
        {/*    >*/}
        {/*      {showAdvanced ? "Hide" : "Show"}{" "}*/}
        {/*      <I18nText>topbar.filter.filters</I18nText>*/}
        {/*      {currentFilters.activeFilterCount > 0 &&*/}
        {/*        ` (${currentFilters.activeFilterCount})`}*/}
        {/*    </Button>*/}
        {/*  )}*/}

        {/*  {currentFilters.hasActiveFilters && (*/}
        {/*    <IconButton*/}
        {/*      aria-label="clear all filters"*/}
        {/*      size="small"*/}
        {/*      onClick={handleClearAllFilters}*/}
        {/*      style={{ marginLeft: 8 }}*/}
        {/*    >*/}
        {/*      <ClearIcon />*/}
        {/*    </IconButton>*/}
        {/*  )}*/}
        {/*</Box>*/}
      </Box>

      {/* Filter Buttons */}
      {hasRole(user, ROLES.MASTER) && (
        <DeviceTypeFilter
          typeFilter={filters.typeFilter}
          onTypeFilterChange={value => updateFilter("typeFilter", value)}
          TYPE_BUTTONS={TYPE_BUTTONS}
        />
      )}

      {/* Active Filter Chips */}
      {showActiveFilters && (
        <ActiveFilterChips
          filters={currentFilters}
          onClearFilter={handleClearFilter}
          TYPE_BUTTONS={TYPE_BUTTONS}
        />
      )}

      {/* Inline Advanced Filters */}
      {showAdvancedByDefault && (
        <Collapse in={showAdvanced}>
          <AdvancedFilters
            filters={filters}
            onFilterChange={updateFilter}
            options={filterOptions}
            contentTypes={contentTypes}
            deviceStatuses={deviceStatuses}
          />
        </Collapse>
      )}

      {/* Filter Dialog */}
      <FilterDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        filters={filters}
        onFilterChange={updateFilter}
        onTypeFilterChange={value => updateFilter("typeFilter", value)}
        onClearAllFilters={handleClearAllFilters}
        hasActiveFilters={currentFilters.hasActiveFilters}
        TYPE_BUTTONS={TYPE_BUTTONS}
        filterOptions={filterOptions}
        contentTypes={contentTypes}
        deviceStatuses={deviceStatuses}
      />
    </Box>
  );
};

export default SearchTermFilter;
